import {call, type Effect, type ForkEffect, put, select, takeEvery, takeLatest} from 'redux-saga/effects';
import {ApiActions} from '@touchnet/react-common';
import {TnToastActions} from '@touchnet/react-toast';
import LOG from 'loglevel';

import CodeService from '../../services/code-service';
import {cast} from "~/utils/fns.ts";
import Selectors from "~/state/selectors.ts";
import {actions} from "~/state/reducers/codes-reducer.ts";

export function* loadCodes(action: ReturnType<typeof actions.loadCodes>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));

    const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
    const serverBase: string = cast<string>(yield select(Selectors.getServerBase));

    try {
        const codes = yield call(CodeService.loadCodes, cNum, serverBase);

        if (codes) {
            yield put(actions.receiveCodes(codes));
        }
    } catch (error) {
        LOG.error('LOAD_CODES: Error calling the api %o', [error]);
        yield put(actions.receiveCodesFailed());
        yield put(TnToastActions.showErrorMessage("Error Loading Accounting Codes"));
    }

    yield put(ApiActions.completeApiCall(action.type));
}

export function* loadOneCode(action: ReturnType<typeof actions.loadOneCode>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));

    const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
    const serverBase: string = cast<string>(yield select(Selectors.getServerBase));

    try {
        const code = yield call(CodeService.loadOneCode, cNum, serverBase, action.payload);

        if (code) {
            yield put(actions.receiveOneCode(code));
        }
    } catch (error) {
        LOG.error('LOAD_ONE_CODE: Error calling the api %o', [error]);
        yield put(actions.receiveOneCodeFailed());
        yield put(TnToastActions.showErrorMessage("Error Loading Accounting Code"));
    }

    yield put(ApiActions.completeApiCall(action.type));
}

export function* updateOneCode(action: ReturnType<typeof actions.updateOneCode>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));

    const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
    const serverBase: string = cast<string>(yield select(Selectors.getServerBase));
    const {adjustedValues: code, navigate, successMessage: message} = action.payload;

    try {
        yield call(CodeService.updateOneCode, cNum, serverBase, code);
        yield put(TnToastActions.showSuccessMessage(message));
        yield call(navigate, `/${cNum}/ac/codes`);
    } catch (error) {
        LOG.error('UPDATE_ONE_CODE: Something went wrong while called the api %o', [error]);
        yield put(TnToastActions.showErrorMessage('Error updating Accounting Code'));
    }

    yield put(ApiActions.completeApiCall(action.type));
}

export function* deleteOneCode(action: ReturnType<typeof actions.deleteOneCode>): IterableIterator<Effect> {
    yield put(ApiActions.beginApiCall(action.type));

    const cNum: string = cast<string>(yield select(Selectors.getCustomerNumber));
    const serverBase: string = cast<string>(yield select(Selectors.getServerBase));
    const codeId: string = action.payload;

    try {
        yield call(CodeService.deleteOneCode, cNum, serverBase, codeId);
        yield put(actions.loadCodes());
    } catch (error) {
        LOG.error('DELETE_ONE_CODE: Error calling the api %o', [error]);
        yield put(TnToastActions.showErrorMessage("Unable to Delete Accounting Code"));
    }

    yield put(ApiActions.completeApiCall(action.type));
}

export default function* rootSaga(): IterableIterator<ForkEffect> {
    yield takeEvery(actions.loadCodes.type, loadCodes);
    yield takeLatest(actions.loadOneCode.type, loadOneCode);
    yield takeEvery(actions.updateOneCode.type, updateOneCode);
    yield takeEvery(actions.deleteOneCode.type, deleteOneCode);
}

